import httpService from '../Api/http.service';
import { LEVELS } from 'global-constants/constants';
import { getApiBaseUrl } from 'utils';

const {
  REACT_APP_API_BASE_URL,
  REACT_APP_FLEX_API_BASE_URL,
} = process.env;
const getManagementBaseUrl = () => `${getApiBaseUrl()}/mgt`;
const getAppCoreBaseUrl = () => `${getApiBaseUrl()}/appcore`;
const USERS_BASE_URL = `${getAppCoreBaseUrl()}/users`;
const FLEX_BASE_URL = `${REACT_APP_FLEX_API_BASE_URL}/v1/`;

const fetchPartnerUsers = (partner_id, params) => {
  const qParams = params ? `?${params}` : '';
  const url = `/users/${partner_id}${qParams}`;

  return httpService({
    url,
    method: 'get',
  });
};

const fetchAllUsers = (level, partner_id, level_id, role) => {
  let url;
  if (partner_id && level === LEVELS.PARTNER) {
    url = `/users/${partner_id}`;
  } else if (partner_id && level === 'revshare') {
    url = `/users/revshare`;
  } else if (partner_id && level && level_id) {
    url = `/users/${partner_id}?${level + '_id'}=${level_id}`;
  } else if (role) {
    url = `/users/subscribili?role=${role}`;
  } else {
    url = `/users/subscribili`;
  }

  return httpService({
    url,
    method: 'get',
  });
};

// New API endpoints for user management with multiple locations/regions
const getAllUsers = (qParams = '') => {
  const query = qParams ? `?${qParams}` : '';
  const url = `${USERS_BASE_URL}${query}`;

  return httpService({
    url,
    method: 'get',
  });
};

const createUserV2 = (payload) =>
  httpService({
    url: USERS_BASE_URL,
    method: 'post',
    data: payload,
  });

const updateUserV2 = (user_id, payload) =>
  httpService({
    url: `${USERS_BASE_URL}/${user_id}`,
    method: 'put',
    data: payload,
  });

const deleteUserV2 = (user_id) =>
  httpService({
    url: `${USERS_BASE_URL}/${user_id}`,
    method: 'delete',
  });

// Legacy endpoints (keeping for backward compatibility)
const createUser = (payload) =>
  httpService({
    url: `/user`,
    method: 'post',
    data: payload,
  });

const deleteUser = (payload) =>
  httpService({
    url: `/user`,
    method: 'delete',
    data: payload,
  });

const updateUser = (payload) =>
  httpService({
    url: `/user`,
    method: 'put',
    data: payload,
  });

const getUser = () =>
  httpService({
    url: `/user`,
    method: 'get',
  });

const fetchUsers = () =>
  httpService({
    url: `/users`,
    method: 'get',
  });

const resetPassword = (payload) =>
  httpService({
    url: getManagementBaseUrl() + `/set-user-password`,
    method: 'post',
    data: payload,
  });

const resetDefaultPassword = (username = '') => {
  const payload = {
    username,
  };

  return httpService({
    url: getManagementBaseUrl() + `/login/reset`,
    method: 'put',
    data: payload,
  });
};

const resetDefaultPasswordV2 = (user_id) => {
  return httpService({
    url: `${USERS_BASE_URL}/reset-password/${user_id}`,
    method: 'post',
  });
};

const getSupportedCountries = () =>
  httpService({
    url: getManagementBaseUrl() + `/supported-countries`,
    method: 'get',
  });

const getAppCoreMenus = () =>
  httpService({
    url: getAppCoreBaseUrl() + '/menus',
    method: 'get',
  });

const getDocumentList = (level, levelId) =>
  httpService({
    url: `${getManagementBaseUrl()}/${level}/${levelId}/supported-documents`,
    method: 'get',
  });

const getSettings = (settingLevel, levelId, params) => {
  let api = params
    ? `/settings/${settingLevel}/${levelId}?${params}`
    : `/settings/${settingLevel}/${levelId}`;

  return httpService({
    url: api,
    method: 'get',
  });
};

const getRoles = () =>
  httpService({
    url: '/settings/roles',
    method: 'get',
  });

const getAWSAccessURL = (key) =>
  httpService({
    url: getManagementBaseUrl() + `/get_access_url?key=` + key,
    method: 'get',
  });

const getAWSAccessURLs = (keys) =>
  httpService({
    url: getManagementBaseUrl() + '/get_access_urls',
    method: 'post',
    data: { keys },
  });

const getAccountLink = (params = '') =>
  httpService({
    url: getManagementBaseUrl() + `/get-account-link${params}`,
    method: 'get',
  });

const updateLevels = (payload, level, formData = false) => {
  const levelName = level === 'revshare' ? 'revshares' : level;
  const serviceData = {
    url: `/${levelName}`,
    method: 'put',
    data: payload,
  };
  formData && (serviceData.headers = { 'Content-Type': 'multipart/form-data' });

  return httpService(serviceData);
};

const accountSetup = (payload, type, id, method = 'post') =>
  httpService({
    url: `/${type}/${id}/setup`,
    method,
    data: payload,
  });

const uploadFile = (payload, group_id) => {
  const serviceData = {
    url: `/group/${group_id}/file`,
    method: 'put',
    data: payload,
  };
  serviceData.headers = { 'Content-Type': 'multipart/form-data' };

  return httpService(serviceData);
};

// One stable key per group per asset type, so an upload replaces the
// image on cards already issued.
const uploadWalletBrandingAsset = (group_id, asset_type, payload) => {
  const serviceData = {
    url: getAppCoreBaseUrl() + `/groups/${group_id}/wallet-branding-assets/${asset_type}`,
    method: 'put',
    data: payload,
  };
  serviceData.headers = { 'Content-Type': 'multipart/form-data' };

  return httpService(serviceData);
};

// Only the types a group has published come back; there is no
// fallback, so an absent type means nothing is published.
const getWalletBrandingAssets = (group_id) => {
  const serviceData = {
    url: getAppCoreBaseUrl() + `/groups/${group_id}/wallet-branding-assets`,
    method: 'get',
  };

  return httpService(serviceData);
};

const deleteFile = (group_id, file_name, payload = {}) => {
  file_name = file_name.replace(/_/g, '-');
  const serviceData = {
    url: `/groups/${group_id}/files/${file_name}`,
    method: 'delete',
    data: payload,
  };
  return httpService(serviceData);
};

const uploadUcrFeesSchedule = (group_id, payload = {}) => {
  const serviceData = {
    url: getAppCoreBaseUrl() + `/files/groups/${group_id}/file`,
    method: 'put',
    data: payload,
  };
  serviceData.headers = { 'Content-Type': 'multipart/form-data' };

  return httpService(serviceData);
};

const deleteUcrFeesSchedule = (group_id, payload = {}) => {
  const serviceData = {
    url: getAppCoreBaseUrl() + `/files/groups/${group_id}/ucr-fees-schedule`,
    method: 'delete',
    data: payload,
  };
  return httpService(serviceData);
};

const getFileFromStripe = (fileId) => {
  const serviceData = {
    url: `/file/${fileId}`,
    method: 'get',
  };

  return httpService(serviceData);
};

const uploadFileToStripe = (payload) => {
  const serviceData = {
    url: `/file`,
    method: 'post',
    data: payload,
  };
  serviceData.headers = { 'Content-Type': 'multipart/form-data' };

  return httpService(serviceData);
};

const getManagementUrl = (path, queryParameters = null) => {
  let queryString = '';

  if (queryParameters && typeof queryParameters === 'object') {
    for (const [key, value] of Object.entries(queryParameters)) {
      queryString += `${queryString ? '&' : '?'}${key}=${value}`;
    }
  }
  return `${getManagementBaseUrl()}${path}${queryString}`;
};

const getTemplate = (path, queryParameters = null) =>
  httpService({
    url: getManagementUrl(path, queryParameters),
    method: 'get',
    headers: {
      Accept: 'application/ms-excel',
    },
    responseType: 'arraybuffer',
  });

const getPaymentTypes = (params = '') =>
  httpService({
    url: getManagementBaseUrl() + `/payment-types`,
    method: 'get',
  });

// Deprecated API, to be removed once Backend deprecates
const getProcedureCategories = (level = '', levelId = '', params = '') =>
  httpService({
    url: getManagementBaseUrl() + `/${level}/${levelId}/fees-summary${params}`,
    method: 'get',
  });

  const getPlanFeeSummary = (
    planPackageId = '',
    level = '',
    levelId = '',
    queryOptions = {}
  ) => {
    const { useDefault = false } = queryOptions;
  
    const baseParams = `level=${level}&level_id=${levelId}`;
    const extraParam = useDefault
      ? `&default=yes`
      : `&plan_package_id=${planPackageId}`;
    const params = `${baseParams}${extraParam}`;
  
    return httpService({
      url: `${getApiBaseUrl()}/loop/plans/fee-summary?${params}`,
      method: 'get',
    });
  };

const postProcedureCategories = (
  level = '',
  levelId = '',
  params = '',
  payload = {},
) =>
  httpService({
    url: getManagementBaseUrl() + `/${level}/${levelId}/fees-summary${params}`,
    method: 'post',
    data: payload,
  });

const postPlanFeeSummary = (
  planPackageId = '',
  level = '',
  levelId = '',
  payload = {},
) =>
  httpService({
    url:
      `${getApiBaseUrl()}/loop/plans/fee-summary` +
      `?plan_package_id=${planPackageId}&level=${level}&level_id=${levelId}`,
    method: 'post',
    data: payload,
  });

const getUserFeatures = () =>
  httpService({
    url: `/users/features`,
    method: 'get',
  });

/** Note  : FLEX API ⚠️ */
const initiatePaymentInTerminal = (paymentConfig) => {
  return httpService({
    url: FLEX_BASE_URL + `payments`,
    method: 'post',
    data: {
      location_id: paymentConfig.locationId,
      amount: paymentConfig.amount,
      processing_fees: paymentConfig.processingFees,
      payment_method: {
        payment_type: 'card',
        payment_method_state: 'terminal',
        id: paymentConfig.terminalId,
      },
    },
  });
};

/** Note  : FLEX API ⚠️ */
const getTerminalPaymentStatus = (paymentId, terminalId) => {
  return httpService({
    url: FLEX_BASE_URL + `terminals/${terminalId}/payments/${paymentId}/status`,
    method: 'get',
  });
};

const userService = {
  createUser,
  deleteUser,
  updateUser,
  getUser,
  fetchPartnerUsers,
  fetchAllUsers,
  fetchUsers,
  getRoles,
  resetPassword,
  resetDefaultPassword,
  resetDefaultPasswordV2,
  getAppCoreMenus,
  getDocumentList,
  getSettings,
  getAWSAccessURL,
  getAWSAccessURLs,
  getAccountLink,
  updateLevels,
  accountSetup,
  uploadFile,
  deleteFile,
  uploadUcrFeesSchedule,
  uploadWalletBrandingAsset,
  getWalletBrandingAssets,
  deleteUcrFeesSchedule,
  getFileFromStripe,
  uploadFileToStripe,
  getPaymentTypes,
  getTemplate,
  getProcedureCategories,
  getPlanFeeSummary,
  postProcedureCategories,
  postPlanFeeSummary,
  getUserFeatures,
  initiatePaymentInTerminal,
  getTerminalPaymentStatus,
  getSupportedCountries,
  // New V2 endpoints
  getAllUsers,
  createUserV2,
  updateUserV2,
  deleteUserV2,
};

export default userService;
